// src/components/PurchasingManagersScorecard.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Group,
  Button,
  Anchor,
  Center,
  Switch,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

// Metrics array for purchasing scorecard based on the screenshot
const metrics = [
  { key: 'header_plannedInventory', label: 'Planned Inventory Spend', isHeader: true },
  { key: 'plannedSpend', label: 'Spend', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'plannedProjectedMarginPercent', label: 'Projected Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'plannedProjectedMarginDollars', label: 'Projected Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_landedInventory', label: 'Landed Inventory Spend', isHeader: true },
  { key: 'landedSpend', label: 'Spend', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'landedProjectedMarginPercent', label: 'Projected Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'landedProjectedMarginDollars', label: 'Projected Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_revenueMargin', label: 'Revenue/Margin', isHeader: true },
  { key: 'revenue', label: 'Revenue', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'grossMarginPercent', label: 'Gross Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'grossMarginDollars', label: 'Gross Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_inventoryHealth', label: 'Inventory Health', isHeader: true },
  { key: 'inventoryHealth0To90Percent', label: '0-90', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryHealth91To180Percent', label: '91-180', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryHealth181To365Percent', label: '181-365', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryHealth365PlusPercent', label: '365+', formatter: (v) => `${(v).toFixed(0)}%` },

  { key: 'header_newVendor', label: 'New Vendor Base Growth', isHeader: true },
  { key: 'newVendorsPOActual', label: '# of PO Actual', formatter: (v) => v },
  { key: 'newVendorsPOPercent', label: '# of PO Pace', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'rfqCount', label: '# of RFQs', formatter: (v) => v },
];

export default function PurchasingManagersScorecard() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // Default from = 7 days ago, to = today
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d;
  });
  const [toDate, setToDate] = useState(() => new Date());

  // Track which period button is active
  const [activePeriod, setActivePeriod] = useState(null);

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openQuery, setOpenQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  // Helper functions for date range buttons
  const getWeekToDate = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(now);
    // Set to previous Sunday (or today if it's Sunday)
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    return {
      from: startOfWeek,
      to: now
    };
  };

  const getMonthToDate = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: startOfMonth,
      to: now
    };
  };

  const getQuarterToDate = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    const startOfQuarter = new Date(now.getFullYear(), quarterStartMonth, 1);
    return {
      from: startOfQuarter,
      to: now
    };
  };

  const getYearToDate = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return {
      from: startOfYear,
      to: now
    };
  };

  // Handle button clicks
  const handleWTD = () => {
    const { from, to } = getWeekToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('WTD');
  };

  const handleMTD = () => {
    const { from, to } = getMonthToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('MTD');
  };

  const handleQTD = () => {
    const { from, to } = getQuarterToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('QTD');
  };

  const handleYTD = () => {
    const { from, to } = getYearToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('YTD');
  };

  // Custom handler for date picker to unselect period buttons
  const handleDateChange = (newDate, type) => {
    if (type === 'from') {
      setFromDate(newDate);
    } else {
      setToDate(newDate);
    }
    // Unselect period buttons when manual date is changed
    setActivePeriod(null);
  };

  // Format YYYY-MM-DD
  const fromParam = fromDate.toISOString().split('T')[0];
  const toParam = toDate.toISOString().split('T')[0];

  const url = `${config.apiBaseUrl}/PurchasingManager/Scorecard?from=${fromParam}&to=${toParam}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  // Build columns + rows
  const { columns, rows, avgJoeCode, otherCode } = useMemo(() => {
    if (!data?.rows) return { columns: [], rows: [], avgJoeCode: null, otherCode: null };

    // Find the Average Joe and Other rows
    const avgJoeCode = data.rows.find((r) => r.isAverageJoe)?.ccode;
    const otherCode = data.rows.find((r) => r.ccode === 'Other')?.ccode || 'Other';

    // Get list of purchasing codes, exclude avgJoe and Other
    const allCodes = Array.from(
      new Set(data.rows.filter((r) => r.onPurchasingTeam && r.ccode !== otherCode).map((r) => r.ccode))
    );

    // Sort codes with avgJoe and Other at the end
    const sortedCodes = [
      ...allCodes.filter((c) => c !== avgJoeCode),
      ...(avgJoeCode ? [avgJoeCode] : []),
      otherCode
    ];

    // Columns: metric + one per person
    const cols = [
      {
        accessorKey: 'metric',
        header: 'Metrics',
        size: 180,
        enablePinning: true,
        enableSorting: false,
        enableColumnFilter: false,
      },
      ...sortedCodes.map((code) => ({
        accessorKey: code,
        header: data.rows.find((r) => r.ccode === code)?.name || code,
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
      })),
    ];

    // Pivot rows
    const pivotRows = metrics.map((m) => {
      const row = {
        id: m.key,
        metric: m.label,
        isHeader: m.isHeader || false
      };

      if (!m.isHeader) {
        sortedCodes.forEach((code) => {
          const rep = data.rows.find((r) => r.ccode === code) || {};
          let raw = rep[m.key];
          row[code] = raw != null ? (m.formatter ? m.formatter(raw) : raw) : '-';
        });
      } else {
        // For header rows, leave cells empty
        sortedCodes.forEach((code) => {
          row[code] = '';
        });
      }

      return row;
    });

    return { columns: cols, rows: pivotRows, avgJoeCode, otherCode };
  }, [data]);

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and filter switch */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Managers Scorecard
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
           onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_managers_scorecard',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Query
        </Anchor>
      </Group>

      {/* Date range controls */}
      <Box mb="sm">
        <Group gap="xs" mb="sm">
          <Button
            size="xs"
            onClick={handleWTD}
            variant={activePeriod === 'WTD' ? 'filled' : 'outline'}
          >
            Week-to-Date
          </Button>
          <Button
            size="xs"
            onClick={handleMTD}
            variant={activePeriod === 'MTD' ? 'filled' : 'outline'}
          >
            Month-to-Date
          </Button>
          <Button
            size="xs"
            onClick={handleQTD}
            variant={activePeriod === 'QTD' ? 'filled' : 'outline'}
          >
            Quarter-to-Date
          </Button>
          <Button
            size="xs"
            onClick={handleYTD}
            variant={activePeriod === 'YTD' ? 'filled' : 'outline'}
          >
            Year-to-Date
          </Button>
        </Group>

        {/* Date pickers */}
        <Group gap="sm">
          <DatePickerInput
            label="From"
            value={fromDate}
            onChange={(newVal) => handleDateChange(newVal, 'from')}
            size="xs"
            style={{ width: 160 }}
          />
          <DatePickerInput
            label="To"
            value={toDate}
            onChange={(newVal) => handleDateChange(newVal, 'to')}
            size="xs"
            style={{ width: 160 }}
          />
        </Group>
      </Box>

      {/* Table container */}
      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable
              columns={columns}
              data={rows}
              enablePinning
              initialState={{
                columnPinning: {
                  left: ['metric'],
                  right: [avgJoeCode, otherCode].filter(Boolean),
                },
                pagination: {
                  pageSize: 50,
                },
                density: 'xs',
              }}
              enableTopToolbar={false}
              mantineTableProps={{
                
                highlightOnHover: true,
                withBorder: true,
                style: { fontSize: '0.75rem' },
              }}
              mantineTableHeadProps={{
                style: { height: '32px' },
              }}
              enableGlobalFilter={false}
              enableColumnFilters={false}
              enableSorting={false}
              enableRowSelection={false}
              getRowId={(row) => row.id}
              mantineTableBodyRowProps={({ row }) => ({
                style: {
                  cursor: 'pointer',
                  backgroundColor: row.original.isHeader ? 'var(--mantine-color-dark-6)' : undefined,
                  color: row.original.isHeader ? 'white' : undefined,
                  fontWeight: row.original.isHeader ? 'bold' : undefined,
                  height: row.original.isHeader ? '28px' : '26px',
                },
              })}
              mantineTableBodyCellProps={({ cell }) => ({
                style: {
                  backgroundColor: cell.row.original.isHeader ? 'var(--mantine-color-dark-6)' : undefined,
                  color: cell.row.original.isHeader ? 'white' : undefined,
                  padding: '2px 6px',
                },
              })}
              enablePagination={false}
              enableBottomToolbar={false}
            />
          )}
        </Box>
      </Paper>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openQuery}
        onClose={() => setOpenQuery(false)}
        title="SQL Query"
        endpoint={`${config.apiBaseUrl}/PurchasingManager/Scorecard/queries/managers-scorecard-query`}
      />
    </Paper>
  );
}